import { useMemo } from 'react';

import type { ContactFieldsFragment } from '@/graphql/types';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SUGGESTIONS_ITEM_BASE_CLASSNAME } from '../../../../form/SuggestionsList';
import AutocompleteInput, { type AutocompleteSuggestion } from '../../../../form/AutocompleteInput';
import { useContactAutocompleteResults } from './useContactAutocompleteResults';
import { isEmailValid } from '../../../../../utils/validators';
import classNames from '../../../../../utils/classNames';

export interface EmailRecipientInputProps {
    value: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    placeholder: string;
    disabled?: boolean;
    name: string;
    onQuickCreate: (email: string, index: number) => void;
    index: number;
}

function toAutocompleteSectionName(value: string) {
    return value.replace(/[^a-zA-Z0-9-]/g, '-');
}

function buildSuggestions(contacts: ContactFieldsFragment[]): AutocompleteSuggestion[] {
    return contacts.reduce<AutocompleteSuggestion[]>((accumulator, contact) => {
        if (!contact.email) {
            return accumulator;
        }

        accumulator.push({
            id: contact.id,
            label: contact.name,
            value: contact.email,
            secondaryLabel: contact.email,
        });
        return accumulator;
    }, []);
}

function QuickCreateAction({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            className={classNames(
                SUGGESTIONS_ITEM_BASE_CLASSNAME,
                'items-center gap-2 text-blue-600 hover:underline'
            )}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={faUserPlus} />
            <span>{label}</span>
        </button>
    );
}

export function EmailRecipientInput({
    value,
    onChange,
    onFocus,
    placeholder,
    disabled,
    name,
    onQuickCreate,
    index,
}: EmailRecipientInputProps) {
    const { contacts, loading: isLoading } = useContactAutocompleteResults(value);
    const suggestions = useMemo(() => buildSuggestions(contacts), [contacts]);
    const canQuickCreate = !disabled && isEmailValid(value);
    // Use a unique autofill section per row so browser suggestions are applied
    // to only this recipient input instead of propagating to other rows.
    const autocompleteSectionName = toAutocompleteSectionName(name);

    const emptyState = canQuickCreate ? (
        <QuickCreateAction
            label={gettext('CREATE_NEW_CONTACT')}
            onClick={() => onQuickCreate(value, index)}
        />
    ) : undefined;

    return (
        <AutocompleteInput
            value={value}
            onValueChange={onChange}
            suggestions={suggestions}
            isLoading={isLoading}
            placeholder={placeholder}
            disabled={disabled}
            name={name}
            onFocus={onFocus}
            autoComplete={`section-${autocompleteSectionName} email`}
            emptyState={emptyState}
        />
    );
}
